import { useCallback, useEffect, useState } from "react";

// NOTE on the storage key:
// Iteration 6 bumped guest credits 1→2 (so the bank matches the 2-credit
// scan cost). Any browser/PWA that visited the site before that change
// still has `{guestCredits: 1}` cached under the legacy key and would
// otherwise be stuck showing "CREDS 1" forever, breaking the new math.
// Versioning the key forces a one-shot reseed for those operators while
// honoring the existing ledger for everyone provisioned post-iter-6.
const STORAGE_KEY = "cmh-guest-v2";
const LEGACY_STORAGE_KEYS = ["truth-scan-hub-guest"];
const DEVICE_KEY = "cmh-guest-device-id";

// Stable per-device UUID so the server can quota guests by device instead
// of by IP — IP-only quotas misfire badly behind mobile CGNAT, corporate
// proxies, Expo Go dev hosts, and CDN egress pools where many real users
// share a single egress address. Backend pairs this with a soft IP cap
// (~20/day) for anti-abuse, so a single device still gets exactly one
// scan per 24h regardless of how many roommates / co-workers are on the
// same network.
const guestDeviceId = () => {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = window.localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        `gd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
};
// Guest visitors land with 2 credits — exactly enough for ONE live scan
// (since every scan, guest or authed, costs SCAN_COST=2). This keeps the
// math consistent across the header "CREDS 2" badge, the "Execute scan ·
// 2 credits" button copy, and the underlying server cost.
const INITIAL_GUEST = { isGuest: true, guestCredits: 2, v: 2 };
const GUEST_SCAN_COST = 2;

const readGuest = () => {
  if (typeof window === "undefined") return INITIAL_GUEST;
  try {
    // Sweep legacy keys so stale browser state can't leak through.
    LEGACY_STORAGE_KEYS.forEach((k) => window.localStorage.removeItem(k));

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_GUEST;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.guestCredits !== "number") return INITIAL_GUEST;
    // Defensive ceiling — never trust client storage to grant more
    // credits than the current INITIAL_GUEST seed.
    if (parsed.guestCredits > INITIAL_GUEST.guestCredits) return INITIAL_GUEST;
    return parsed;
  } catch {
    return INITIAL_GUEST;
  }
};

const writeGuest = (state) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage blocked */
  }
};

/**
 * Tiny client-side guest-session ledger.
 *
 *   • Provisions `{ isGuest: true, guestCredits: 2 }` on first load — one
 *     full scan's worth at 2 credits per scan.
 *   • `spendGuestCredit()` debits `GUEST_SCAN_COST` (2) from the ledger
 *     and returns true if the spend was allowed.
 *   • `guestExhausted` flips true the instant the operator can no longer
 *     afford another scan (credits < 2).
 *   • `deviceId` is the stable per-device UUID — exposed so the API
 *     layer can attach it to `/api/scan/guest` calls (server quotas by
 *     device, not by raw IP).
 *
 * The server-side throttle in `/api/scan/guest` (1 per device / 24h)
 * is the actual gate; this hook only powers the UI flow.
 */
const useGuestSession = () => {
  const [guest, setGuest] = useState(readGuest);
  const [deviceId] = useState(guestDeviceId);

  useEffect(() => {
    writeGuest(guest);
  }, [guest]);

  const spendGuestCredit = useCallback(() => {
    let allowed = false;
    setGuest((prev) => {
      if ((prev?.guestCredits || 0) < GUEST_SCAN_COST) return prev;
      allowed = true;
      return { ...prev, guestCredits: prev.guestCredits - GUEST_SCAN_COST };
    });
    return allowed;
  }, []);

  const resetGuest = useCallback(() => setGuest(INITIAL_GUEST), []);

  return {
    guest,
    deviceId,
    guestExhausted: (guest?.guestCredits || 0) < GUEST_SCAN_COST,
    spendGuestCredit,
    resetGuest,
  };
};

export default useGuestSession;
